import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import type { ApiEnvelope } from '@/shared/api/repositories/http-repositories'
import { useBusinessStore } from '@/stores/business-store'
import { refreshAccessToken, request } from '@/utils/http/client'
import { tokenStorage } from '@/utils/http/token-storage'

interface UserIdentity {
  publicId: string
  phone: string
  displayName: string
  creditBalance: number
}

interface AuthState {
  userPhone: string | null
  userDisplayName: string | null
  user: UserIdentity | null
  hydrated: boolean
  bootstrap: () => Promise<void>
  loginUser: (phone: string, code: string, agreement: boolean) => Promise<void>
  loginWithCasdoor: (code: string, state: string) => Promise<void>
  logoutUser: () => Promise<void>
  expireSession: () => void
}

export const DEV_USER = {
  phone: import.meta.env.VITE_DEV_LOGIN_PHONE ?? '',
  code: import.meta.env.VITE_DEV_LOGIN_CODE ?? '',
} as const

const AUTH_STORAGE_KEY = 'video-transcript-auth-v4'
const LEGACY_AUTH_STORAGE_KEY = 'video-transcript-auth-v3'

if (typeof sessionStorage !== 'undefined') {
  sessionStorage.removeItem(LEGACY_AUTH_STORAGE_KEY)
}

const userFrom = (value: {
  user: { public_id: string; phone: string; display_name?: string }
  credit_balance: number
}): UserIdentity => ({
  publicId: value.user.public_id,
  phone: value.user.phone,
  displayName: value.user.display_name || value.user.phone,
  creditBalance: value.credit_balance,
})

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      userPhone: null,
      userDisplayName: null,
      user: null,
      hydrated: false,
      bootstrap: async () => {
        try {
          if (!tokenStorage.getAccess()) {
            await refreshAccessToken()
          }
          const response = await request<
            ApiEnvelope<{
              user: { public_id: string; phone: string; display_name?: string }
              credit_balance: number
            }>
          >({ method: 'GET', url: '/auth/me' })
          const user = userFrom(response.data)
          set({ user, userPhone: user.phone, userDisplayName: user.displayName, hydrated: true })
        } catch {
          tokenStorage.clear()
          set({ hydrated: true, userPhone: null, userDisplayName: null, user: null })
        }
      },
      loginUser: async (phone, code, agreement) => {
        if (!agreement) throw new Error('请先阅读并同意服务条款与隐私政策')
        const response = await request<
          ApiEnvelope<{
            access_token: string
            user: { public_id: string; phone: string; display_name?: string }
            credit_balance: number
          }>
        >({
          method: 'POST',
          url: '/auth/login',
          data: { phone, code, agreement_version: '2026-07-31' },
          skipAuth: true,
          skipRefresh: true,
        })
        const user = userFrom(response.data)
        tokenStorage.set(response.data.access_token)
        useBusinessStore.getState().clear()
        set({ user, userPhone: user.phone, userDisplayName: user.displayName, hydrated: true })
      },
      logoutUser: async () => {
        let casdoorLogoutUrl = ''
        try {
          const result = await request<
            ApiEnvelope<{ logged_out: boolean; casdoor_logout_url?: string }>
          >({
            method: 'POST',
            url: '/auth/logout',
          })
          casdoorLogoutUrl = result.data?.casdoor_logout_url ?? ''
        } finally {
          tokenStorage.clear()
          useBusinessStore.getState().clear()
          set({ user: null, userPhone: null, userDisplayName: null })
        }
        if (casdoorLogoutUrl) {
          const returnUrl = encodeURIComponent(window.location.origin + '/')
          window.location.href = `${casdoorLogoutUrl}?returnUrl=${returnUrl}`
        }
      },
      loginWithCasdoor: async (code, state) => {
        const response = await request<
          ApiEnvelope<{
            access_token: string
            user: { public_id: string; phone: string; display_name?: string }
            credit_balance: number
          }>
        >({
          method: 'POST',
          url: '/auth/casdoor/callback',
          data: { code, state },
          skipAuth: true,
          skipRefresh: true,
        })
        const user = userFrom(response.data)
        tokenStorage.set(response.data.access_token)
        useBusinessStore.getState().clear()
        set({ user, userPhone: user.phone, userDisplayName: user.displayName, hydrated: true })
      },
      expireSession: () => {
        useBusinessStore.getState().clear()
        set({ user: null, userPhone: null, userDisplayName: null, hydrated: true })
      },
    }),
    {
      name: AUTH_STORAGE_KEY,
      storage: createJSONStorage(() => sessionStorage),
      partialize: ({ userPhone, userDisplayName, user }) => ({
        userPhone,
        userDisplayName,
        user,
      }),
    },
  ),
)
